// src/hooks/useConsumptionApi.tsx

import { API } from 'aws-amplify'
import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  createContext,
  ReactNode,
  SetStateAction,
  Dispatch,
} from 'react'

import { DashboardId, FolderId } from '@/types/domain'
import { ConsumptionPool } from '@/types/domain/consumption'

import { authHeader, colorSchemeConsumption, notifyError } from '../api/'
import { usePreloader } from '../components/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env
const BAD_REQUEST = 'Bad Request'

const mapPeriod = p =>
  p === 'yy' ? 'y' : p === 'y' ? 'm' : p === 'm' ? 'd' : 'h'

type FactorError = { factorError: true; sensor?: string }
type ConsumptionResult = unknown

type ConsumptionCache = Map<number, ConsumptionResult | FactorError>
type InflightCache = Map<string, Promise<unknown>>

type ConsumptionContextValue = {
  data: ConsumptionCache
  setData: Dispatch<SetStateAction<ConsumptionCache>>
  inflight: InflightCache
  invalidate: (_: string | number) => void
}

const ConsumptionCtx = createContext<ConsumptionContextValue>({
  data: new Map<number, ConsumptionResult | FactorError>(),
  setData: () => {},
  inflight: new Map<string, Promise<unknown>>(),
  invalidate: () => {},
})

type Props = {
  children: ReactNode
}

export const ConsumptionDataProvider = ({ children }: Props) => {
  const [dashboards, setDashboards] = useState<ConsumptionCache>(
    () => new Map()
  )
  const inflight = useRef<InflightCache>(new Map())

  const invalidate = useCallback((id: string | number) => {
    const key = `dash:${Number(id)}`
    inflight.current.delete(key)
    setDashboards(prev => {
      const next = new Map(prev)
      next.delete(Number(id))
      return next
    })
  }, [])

  const value = useMemo(
    () => ({
      data: dashboards,
      setData: setDashboards,
      inflight: inflight.current,
      invalidate,
    }),
    [dashboards, invalidate]
  )

  return (
    <ConsumptionCtx.Provider value={value}>{children}</ConsumptionCtx.Provider>
  )
}

const fetchFolderRaw = async (id: FolderId, from, to, period) => {
  await refreshToken()

  const url = `${REACT_APP_API_URL}/consumption/folder/${id}?from=${from}&to=${to}&period=${period}`
  const options = { headers: { ...authHeader() }, method: 'GET' }
  const result = await fetch(url, options)

  if (!result.ok) {
    const text = await result.text()
    if (result.status === 400 && (text || '').includes(BAD_REQUEST)) {
      const sensor = (text.split('for')[1] || '').replace(/.$/, '').trim()
      return { factorError: true, sensor }
    }
    throw {
      message: text || `HTTP ${result.status}`,
      status: result.status,
    }
  }
  return result.json()
}

const fetchMeterReadingRaw = async (id: FolderId, from, to, granularity) => {
  await refreshToken()

  const url = `${REACT_APP_API_URL}/meter/folder/${id}?from=${from + ' 00:00:00'}&to=${to + ' 23:59:59'}&granularity=${granularity}`
  const options = { headers: { ...authHeader() }, method: 'GET' }
  const result = await fetch(url, options)
  if (!result.ok) {
    const text = await result.text()
    if (result.status === 400 && (text || '').includes(BAD_REQUEST)) {
      const sensor = (text.split('for')[1] || '').replace(/.$/, '').trim()
      return { factorError: true, sensor }
    }

    throw {
      message: text || `HTTP ${result.status}`,
      status: result.status,
    }
  }
  return result.json()
}

export const useConsumptionApi = () => {
  const { start, stop } = usePreloader()

  const withRequest = useCallback(async fn => {
    try {
      return await fn()
    } catch (error: any) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          notifyError(`ConsumptionApi2: ${e2?.message}`)
          return null
        }
      }
      // suppress error as backend fires 500 on empty data sets
      // which is not really an error in this case
      //notifyError(`ConsumptionApi1: ${error?.message}`)
      if (status !== 500) return null
      return null
    }
  }, [])

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const {
    data: dashboardCache,
    setData: setDashboardCache,
    inflight,
    invalidate,
  } = useContext(ConsumptionCtx)
  const dashboardCacheRef = useRef(dashboardCache)

  useEffect(() => {
    dashboardCacheRef.current = dashboardCache
  }, [dashboardCache])

  const fetchDashboardRaw = useCallback(
    async (id: DashboardId) => {
      if (!Number.isFinite(id) || id <= 0) return []
      const cached = dashboardCacheRef.current?.get?.(id)
      if (cached) return cached
      const key = `dash:${id}`
      if (inflight.has(key)) return inflight.get(key)
      const API_NAME = 'karmaApi'

      await refreshToken()

      const endpoint = `/consumption/dashboard/${id}`
      const options = { method: 'GET', headers: { ...authHeader() } }
      const exec = () => API.get(API_NAME, endpoint, options)
      const p = exec()
        .then(result => {
          setDashboardCache(prev => {
            const next = new Map(prev)
            next.set(id, result)
            return next
          })
          return result
        })
        .catch(error => {
          const { data, status } = error?.response || {}
          const { message } = data || {}
          if (
            status === 400 &&
            typeof message === 'string' &&
            message.includes(BAD_REQUEST)
          ) {
            const sensor = message.split('for')[1]?.replace(/.$/, '')?.trim()
            const factorErr = { factorError: true, sensor }
            setDashboardCache(prev => {
              const next = new Map(prev)
              next.set(id, factorErr)
              return next
            })
            return factorErr
          }
          throw {
            message: error?.message || 'request failed',
            status,
          }
        })
        .finally(() => {
          inflight.delete(key)
        })
      inflight.set(key, p)
      return p
    },
    [setDashboardCache, inflight]
  )

  const getFolderCompareConsumption = useCallback(
    (id: FolderId, period, fromDate, toDate) =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id || !fromDate || !toDate) return []
          const key = `folder:compare:${id}:${fromDate}:${toDate}:${period}`
          const existing =
            (inflight && inflight.get && inflight.get(key)) || null
          if (existing) return existing
          const per = mapPeriod(period)
          const promise = fetchFolderRaw(id, fromDate, toDate, per)
            .then(json => {
              if (json?.factorError) return json
              const list = json?.data ?? []
              return list.map((entry, idx) => ({
                ...entry.attributes,
                entity_id: Number(entry.id),
                consumption: entry.attributes.consumptions,
                chartColor:
                  colorSchemeConsumption[idx % colorSchemeConsumption.length],
              }))
            })
            .finally(() => inflight.delete(key))
          inflight.set(key, promise)
          return promise
        })
      ).then(res =>
        res && res.factorError ? res : Array.isArray(res) ? res : []
      ),
    [withRequest, withPreloader, inflight]
  )

  type AnyRecord = Record<string, any>

  const getDashboardConsumption = useCallback(
    (id: DashboardId): Promise<ConsumptionPool> =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id) return []
          const result = await fetchDashboardRaw(id)
          if (result?.factorError) return result
          const data: AnyRecord[] = Array.isArray((result as AnyRecord)?.data)
            ? ((result as AnyRecord).data as AnyRecord[])
            : []
          const referencedAsSuccessor = new Set(
            data.map(d => d.attributes?.successor_id).filter(id => id !== null)
          )
          const predecessorSensors = data.filter(s =>
            referencedAsSuccessor.has(s.id)
          )
          const successorSensors = data.filter(
            s => s.attributes?.successor_id === null
          )
          const predecessorMap = new Map(predecessorSensors.map(s => [s.id, s]))
          const successorMap = new Map(successorSensors.map(s => [s.id, s]))
          return data.map(s => {
            const pred = predecessorMap.get(s.id) || null
            const succ = successorMap.get(s.id) || null
            return {
              ...s,
              attributes: {
                ...s.attributes,
                has_predecessor: !!pred,
                predecessor_id: pred?.id ?? null,
                predecessor_data_sampling_date:
                  pred?.attributes?.data_sampling_date ?? null,
                predecessor_has_consumptions:
                  (pred?.attributes?.consumptions?.values?.length ?? 0) > 0,
                predecessor_has_heating_corrected_consumptions:
                  (pred?.attributes?.heating_corrected_consumptions?.values
                    ?.length ?? 0) > 0
                    ? true
                    : false,
                predecessor_has_cooling_corrected_consumptions:
                  (pred?.attributes?.cooling_corrected_consumptions?.values
                    ?.length ?? 0) > 0,
                has_successor: !!succ,
                successor_id: succ?.id ?? null,
                successor_data_sampling_date:
                  succ?.attributes?.data_sampling_date ?? null,
                successor_has_consumptions:
                  (succ?.attributes?.consumptions?.values?.length ?? 0) > 0,
                successor_has_heating_corrected_consumptions:
                  (succ?.attributes?.heating_corrected_consumptions?.values
                    ?.length ?? 0) > 0,
                successor_has_cooling_corrected_consumptions:
                  (succ?.attributes?.cooling_corrected_consumptions?.values
                    ?.length ?? 0) > 0,
              },
            }
          })
        })
      ).then(res =>
        res && res.factorError ? res : Array.isArray(res) ? res : []
      ),
    [withRequest, fetchDashboardRaw, withPreloader]
  )

  const getFolderConsumption = useCallback(
    (id: FolderId, period, fromDate, toDate) =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id) return []
          const key = `folder:${id}:${fromDate}:${toDate}:${period}`
          const existing =
            (inflight && inflight.get && inflight.get(key)) || null
          if (existing) return existing
          const per = mapPeriod(period)
          const promise = fetchFolderRaw(id, fromDate, toDate, per)
            .then(json => {
              if (json?.factorError) return json
              const list = json?.data ?? []
              return list.map((entry, idx) => ({
                ...entry.attributes,
                entity_id: Number(entry.id),
                consumption: entry.attributes.consumptions,
                heating_corrected_consumptions:
                  entry.attributes.heating_corrected_consumptions,
                chartColor:
                  colorSchemeConsumption[idx % colorSchemeConsumption.length],
              }))
            })
            .finally(() => inflight.delete(key))
          inflight.set(key, promise)
          return promise
        })
      ).then(res =>
        res && res.factorError ? res : Array.isArray(res) ? res : []
      ),
    [withRequest, withPreloader, inflight]
  )

  const getDailyConsumptionToDate = useCallback(
    (id: FolderId) =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id) return []
          const endDateDay = new Date()
          endDateDay.setDate(new Date().getDate() - 1)
          const fromDate = `${endDateDay.toISOString().split('T')[0]} 00:00:00`
          const toDate = `${new Date().toISOString().split('T')[0]} 23:59:59`
          const key = `folder:dailyToDate:${id}:${fromDate}:${toDate}`
          const existing =
            (inflight && inflight.get && inflight.get(key)) || null
          if (existing) return existing
          const promise = fetchFolderRaw(id, fromDate, toDate, 'h')
            .then(json => {
              if (json?.factorError) return json
              const list = json?.data ?? []
              return list.map(entry => ({
                ...entry.attributes,
                entity_id: Number(entry.id),
              }))
            })
            .finally(() => inflight.delete(key))
          inflight.set(key, promise)
          return promise
        })
      ).then(res =>
        res && res.factorError ? res : Array.isArray(res) ? res : []
      ),
    [withRequest, withPreloader, inflight]
  )

  const getMonthlyConsumptionToDate = useCallback(
    (id: FolderId) =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id) return []
          const endDate = new Date()
          endDate.setMonth(new Date().getMonth() - 1)
          const fromDate = `${endDate.toISOString().split('T')[0]} 00:00:00`
          const toDate = `${new Date().toISOString().split('T')[0]} 23:59:59`
          const key = `folder:monthlyToDate:${id}:${fromDate}:${toDate}`
          const existing =
            (inflight && inflight.get && inflight.get(key)) || null
          if (existing) return existing
          const promise = fetchFolderRaw(id, fromDate, toDate, 'd')
            .then(json => {
              if (json?.factorError) return json
              const list = json?.data ?? []
              return list.map(entry => ({
                ...entry.attributes,
                entity_id: Number(entry.id),
              }))
            })
            .finally(() => inflight.delete(key))
          inflight.set(key, promise)
          return promise
        })
      ).then(res =>
        res && res.factorError ? res : Array.isArray(res) ? res : []
      ),
    [withRequest, withPreloader, inflight]
  )

  const getYearlyConsumptionToDate = useCallback(
    (id: FolderId) =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id) return []
          const fromDateYear = new Date()
          fromDateYear.setFullYear(new Date().getFullYear() - 1)
          fromDateYear.setMonth(new Date().getMonth() + 1)
          fromDateYear.setDate(1)
          const toDateYear = new Date(
            new Date().getFullYear(),
            new Date().getMonth() + 1,
            0
          )
          const fromDate = `${fromDateYear.toISOString().split('T')[0]} 00:00:00`
          const toDate = `${toDateYear.toISOString().split('T')[0]} 23:59:59`
          const key = `folder:yearlyToDate:${id}:${fromDate}:${toDate}`
          const existing =
            (inflight && inflight.get && inflight.get(key)) || null
          if (existing) return existing
          const promise = fetchFolderRaw(id, fromDate, toDate, 'm')
            .then(json => {
              if (json?.factorError) return json
              const list = json?.data ?? []
              return list.map(entry => ({
                ...entry.attributes,
                entity_id: Number(entry.id),
              }))
            })
            .finally(() => inflight.delete(key))
          inflight.set(key, promise)
          return promise
        })
      ).then(res =>
        res && res.factorError ? res : Array.isArray(res) ? res : []
      ),
    [withRequest, withPreloader, inflight]
  )

  const getDecadlyConsumptionToDate = useCallback(
    (id: FolderId) =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id) return []

          const fromDateYear = new Date()
          fromDateYear.setFullYear(new Date().getFullYear() - 10)
          fromDateYear.setMonth(new Date().getMonth() + 1)
          fromDateYear.setDate(1)

          const fromDate = `${fromDateYear.toISOString().split('T')[0]} 00:00:00`
          const year = new Date().getFullYear()
          const toDate = `${year}-12-31 23:59:59`
          const key = `folder:decadlyToDate:${id}:${fromDate}:${toDate}`

          const existing =
            (inflight && inflight.get && inflight.get(key)) || null

          if (existing) return existing

          const promise = fetchFolderRaw(id, fromDate, toDate, 'y')
            .then(json => {
              if (json?.factorError) return json
              const list = json?.data ?? []
              return list.map(entry => ({
                ...entry.attributes,
                entity_id: Number(entry.id),
              }))
            })
            .finally(() => inflight.delete(key))

          inflight.set(key, promise)

          return promise
        })
      ).then(res =>
        res && res.factorError ? res : Array.isArray(res) ? res : []
      ),
    [withRequest, withPreloader, inflight]
  )

  const getFolderMeterReading = useCallback(
    (id: FolderId, fromDate: string, toDate: string, granularity: string) =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id) return []
          const key = `meter:${id}:${fromDate}:${toDate}:${granularity}`
          const existing =
            (inflight && inflight.get && inflight.get(key)) || null
          if (existing) return existing
          const promise = fetchMeterReadingRaw(
            id,
            fromDate,
            toDate,
            granularity
          )
            .then(json => {
              if (json?.factorError) return json
              const list = json?.data ?? []
              return list.map(entry => ({
                ...entry.attributes,
                entity_id: Number(entry.id),
              }))
            })
            .finally(() => inflight.delete(key))
          inflight.set(key, promise)
          return promise
        })
      ).then(res =>
        res && res.factorError ? res : Array.isArray(res) ? res : []
      ),
    [withRequest, withPreloader, inflight]
  )

  return {
    getFolderCompareConsumption,
    getDashboardConsumption,
    getFolderConsumption,
    getDailyConsumptionToDate,
    getMonthlyConsumptionToDate,
    getYearlyConsumptionToDate,
    getDecadlyConsumptionToDate,
    getFolderMeterReading,
    invalidate,
  }
}
